// src/pages/MisCertificadosPage.js
// Página pública dedicada de certificados — hero + header configurable +
// cuadros horizontales (emitido: ver/descargar · en progreso: barra de avance).
// La personalización vive en proyecto.config_json.certificado.front (admin).
import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectContext';
import { usePageLayout } from '../contexts/PageLayoutContext';
import CertificadosStreaming from '../components/certificados/CertificadosStreaming';
import { certificadoService, encuestaService, misPaginasService, BACKEND_BASE } from '../services/api';
import SectionRenderer from '../components/landing/SectionRenderer';
import { useIdioma } from '../contexts/IdiomaContext';
import { downloadBlob } from '../utils/downloadHelper';
import { toast } from 'react-toastify';
import EncuestaModal from '../components/EncuestaModal';

const DEF = {
  primary: '#0f355b',
  accent: '#14b6cb',
  text: '#374151',
  textLight: '#6b7280',
  textMuted: '#9ca3af',
  border: '#e5e7eb',
  bg: '#f8fafc',
  white: '#ffffff',
  success: '#059669',
};

// ============================================================
const Wrap = styled.div`
  min-height: 100vh;
  background: ${p => p.$bg || DEF.bg};
  font-family: ${p => p.$font || "'Segoe UI', sans-serif"};
`;

const CustomHeader = styled.header`
  background: ${p => p.$color};
  color: #fff;
  padding: 0.9rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.12);
  img { height: 38px; width: auto; object-fit: contain; }
  span { font-weight: 700; font-size: 1.05rem; }
`;

const CustomFooter = styled.footer`
  background: ${p => p.$color};
  color: #fff;
  padding: 1.5rem;
  margin-top: 2.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  text-align: center;
  img { height: 40px; width: auto; object-fit: contain; }
  span { font-size: 0.9rem; opacity: 0.95; }
`;

const Hero = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: ${p => p.$align === 'right' ? 'flex-end' : p.$align === 'center' ? 'center' : 'flex-start'};
  text-align: ${p => p.$align || 'left'};
  min-height: ${p => p.$altura || 240}px;
  padding: 1.5rem 6%;
  color: #fff;
  background-color: ${p => p.$img ? '#222' : p.$color};
  background-image: ${p => {
    const ov = p.$overlay ? `linear-gradient(rgba(0,0,0,${p.$opac}), rgba(0,0,0,${p.$opac})), ` : '';
    return p.$img ? `${ov}url('${p.$img}')` : `linear-gradient(135deg, ${p.$color} 0%, #1a4a7a 100%)`;
  }};
  background-size: ${p => p.$size || 'cover'};
  background-position: ${p => p.$pos || 'center'};
  background-repeat: no-repeat;

  @media (max-width: 640px) {
    align-items: center;
    text-align: center;
    min-height: ${p => Math.round((p.$altura || 240) * 0.72)}px;
    padding: 1.25rem 5%;
    background-size: cover;
  }
`;

const HeroText = styled.div`
  margin: 0.12em 0;
  line-height: 1.15;
  font-size: ${p => p.$size || 24}px;
  color: ${p => p.$color || '#fff'};
  font-weight: ${p => p.$peso || 'bold'};
  text-shadow: 0 1px 6px rgba(0,0,0,0.25);
  @media (max-width: 640px) { font-size: ${p => Math.max(14, Math.round((p.$size || 24) * 0.62))}px; }
`;

const Container = styled.div`
  max-width: ${p => p.$max || '920px'};
  margin: 0 auto;
  padding: ${p => p.$padding || '2rem 1.5rem 3rem'};
`;

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  background: ${p => p.$fondo || DEF.white};
  border: ${p => p.$borde || `1.5px solid ${p.$color}33`};
  border-radius: ${p => p.$radio || '14px'};
  padding: ${p => p.$padding || '1.1rem 1.5rem'};
  margin-bottom: ${p => p.$separacion || '1rem'};
  box-shadow: ${p => p.$sombra || '0 1px 4px rgba(0,0,0,0.04)'};
  transition: box-shadow 0.2s, transform 0.2s;
  &:hover { box-shadow: ${p => p.$sombraHover || p.$sombra || '0 4px 16px rgba(0,0,0,0.09)'}; transform: translateY(-1px); }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    text-align: center;
  }
`;

const DateCol = styled.div`
  min-width: 135px;
  text-align: center;
  color: ${p => p.$color || DEF.textMuted};
  font-size: 1.05rem;
  flex-shrink: 0;
`;

const Pill = styled.span`
  flex-shrink: 0;
  background: ${p => p.$bg || '#3aae5a'};
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.25rem 0.7rem;
  border-radius: 4px;
`;

const Divider = styled.div`
  width: 3px;
  align-self: stretch;
  min-height: 38px;
  background: ${p => p.$color};
  border-radius: 3px;
  flex-shrink: 0;
  @media (max-width: 640px) { display: none; }
`;

const CardBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const CursoName = styled.div`
  font-weight: 700;
  color: ${p => p.$color || DEF.primary};
  font-size: 1rem;
  margin-bottom: 0.2rem;
`;

const Bar = styled.div`
  height: 9px;
  background: ${DEF.border};
  border-radius: 5px;
  overflow: hidden;
  margin: 0.55rem 0 0.35rem;
`;

const Fill = styled.div`
  height: 100%;
  width: ${p => p.$pct}%;
  background: linear-gradient(90deg, ${p => p.$color}, ${DEF.accent});
  border-radius: 5px;
  transition: width 0.6s;
`;

const Falta = styled.div`
  font-size: 0.78rem;
  color: ${DEF.textMuted};
  @media (max-width: 640px) { text-align: center; }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
  @media (max-width: 640px) { justify-content: center; }
`;

const Btn = styled.button`
  background: ${p => p.$ghost ? 'transparent' : p.$color};
  color: ${p => p.$ghost ? p.$color : '#fff'};
  border: ${p => p.$ghost ? `1.5px solid ${p.$color}` : 'none'};
  padding: 0.55rem 1.3rem;
  border-radius: ${p => p.$radio || '9px'};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { filter: brightness(1.08); transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

const PctTag = styled.span`
  font-weight: 700;
  color: ${p => p.$color};
  font-size: 0.85rem;
`;

/* Aviso que reemplaza a los botones cuando falta responder la encuesta. */
const AvisoEncuesta = styled.div`
  background: ${p => p.$fondo || DEF.white};
  border-radius: ${p => p.$radio || '4px'};
  box-shadow: ${p => p.$sombra || '0 2px 14px rgba(3,32,64,0.10)'};
  padding: ${p => p.$padding || '1.6rem 1.5rem 1.8rem'};
  margin-bottom: 1rem;
  text-align: center;
`;

const AvisoIcono = styled.div`
  color: ${p => p.$color || '#0f2f5c'};
  margin-bottom: 0.9rem;
  svg { display: inline-block; }
`;

const AvisoTexto = styled.div`
  color: ${p => p.$color || '#0f2f5c'};
  font-size: ${p => p.$size || '1.05rem'};
  font-weight: ${p => p.$peso || '600'};
  margin-bottom: 1rem;
`;

const AvisoBoton = styled.button`
  background: ${p => p.$bg || '#0b76a8'};
  color: ${p => p.$color || '#ffffff'};
  border: none;
  border-radius: ${p => p.$radio || '999px'};
  padding: ${p => p.$padding || '0.7rem 1.6rem'};
  font-weight: 700;
  font-size: ${p => p.$size || '0.92rem'};
  letter-spacing: 0.4px;
  cursor: pointer;
  transition: filter 0.2s, transform 0.2s;
  &:hover { filter: brightness(1.08); transform: translateY(-1px); }
`;

const IconoX = ({ size = 44 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <circle cx="24" cy="24" r="21" stroke="currentColor" strokeWidth="4" />
    <path d="M16 16 L32 32 M32 16 L16 32" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
  </svg>
);

/* ---- Formato "tabla" (opcional): Nombre · Progreso · Estado · Acciones ---- */
const TablaWrap = styled.div`
  overflow-x: auto;
  border: 1px solid ${p => p.$borde || DEF.border};
  border-radius: ${p => p.$radio || '4px'};
  background: ${p => p.$fondo || DEF.white};
`;

const Tabla = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${p => p.$size || '0.95rem'};
  th, td { padding: ${p => p.$padding || '1.05rem 1.2rem'}; text-align: left; vertical-align: middle; }
  th {
    color: ${p => p.$colorTh || DEF.primary};
    font-weight: 700;
    border-bottom: 1px solid ${p => p.$borde || DEF.border};
    background: ${p => p.$fondoTh || 'transparent'};
    white-space: nowrap;
  }
  tbody tr { border-bottom: 1px solid ${p => p.$borde || DEF.border}; }
  tbody tr:last-child { border-bottom: none; }
  tbody tr:nth-child(even) { background: ${p => p.$fondoPar || 'transparent'}; }
  td { color: ${p => p.$colorTd || DEF.textLight}; }
`;

const PctChip = styled.span`
  display: inline-block;
  min-width: 74px;
  text-align: center;
  padding: 0.3rem 0.7rem;
  border-radius: 4px;
  font-size: 0.78rem;
  font-weight: 700;
  color: ${p => p.$completo ? '#ffffff' : (p.$color || DEF.textLight)};
  background: ${p => p.$completo ? (p.$bg || '#3aae5a') : (p.$bgVacio || '#f1f5f9')};
  border: ${p => p.$completo ? 'none' : `1px solid ${DEF.border}`};
`;

const Pendiente = styled.span`
  color: ${p => p.$color || '#e11d48'};
  font-weight: 700;
`;

/* Botón de LinkedIn: mismo alto que "Ver certificado", en versión contorno. */
const BtnLinkedIn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: ${p => p.$bg || '#0a66c2'};
  color: ${p => p.$color || '#ffffff'};
  border: ${p => p.$borde || 'none'};
  padding: 0.55rem 1.1rem;
  border-radius: ${p => p.$radio || '9px'};
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { filter: brightness(1.08); transform: translateY(-1px); }
  svg { flex-shrink: 0; }
`;

/* Paso previo a LinkedIn: el texto de la publicación ya copiado, para pegarlo. */
const FondoCompartir = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;
const CajaCompartir = styled.div`
  background: #ffffff;
  color: #0f172a;
  border-radius: 14px;
  width: 100%;
  max-width: 520px;
  padding: 1.4rem;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  h3 { margin: 0 0 0.4rem; font-size: 1.1rem; color: #0f355b; }
  p { margin: 0 0 0.8rem; font-size: 0.9rem; color: #475569; }
  textarea {
    width: 100%;
    box-sizing: border-box;
    min-height: 140px;
    padding: 0.7rem;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    font: inherit;
    font-size: 0.88rem;
    resize: vertical;
  }
`;
const AccionesCompartir = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  justify-content: flex-end;
  margin-top: 1rem;
  button {
    background: none;
    border: 1px solid #cbd5e1;
    color: #334155;
    padding: 0.55rem 1rem;
    border-radius: 9px;
    font-weight: 600;
    cursor: pointer;
  }
`;

const IconoLinkedIn = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
  </svg>
);

/* Título sobre el listado, con la línea de acento del diseño de la página. */
const TituloLista = styled.h1`
  margin: ${p => p.$margen || '0 0 1.6rem'};
  color: ${p => p.$color || DEF.primary};
  font-size: ${p => p.$size || '2rem'};
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: ${p => p.$transform || 'none'};
  ${p => p.$linea ? `
    padding-bottom: 0.7rem;
    border-bottom: ${p.$lineaAlto || '3px'} solid transparent;
    border-image: ${p.$linea} 1;
  ` : ''}
  @media (max-width: 640px) { font-size: ${p => p.$sizeMovil || '1.5rem'}; }
`;

const Empty = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: ${DEF.textMuted};
  background: ${DEF.white};
  border: 1px dashed ${DEF.border};
  border-radius: 12px;
`;

// ============================================================
const MisCertificadosPage = () => {
  const { user } = useAuth();
  const { proyecto } = useProject();
  const { setPageLayout } = usePageLayout();
  const { idioma, t: tUI } = useIdioma();
  const [data, setData] = useState({ emitidos: [], en_progreso: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [encModal, setEncModal] = useState(null); // { encuesta, proyectoId, certKey, proceed }
  const [encSubmitting, setEncSubmitting] = useState(false);
  const [compartirLi, setCompartirLi] = useState(null); // { texto, url, copiado }

  const front = proyecto?.config_json?.certificado?.front || {};
  const color = front.color_primario || DEF.primary;
  const heroImg = front.hero_imagen ? `${BACKEND_BASE}/${front.hero_imagen}` : '';
  const heroTextos = (front.hero_textos && front.hero_textos.length) ? front.hero_textos : [
    { visible: true, texto: front.hero_titulo || 'Mis Certificados', size: 34, color: '#ffffff', peso: 'bold' },
    { visible: true, texto: front.hero_subtitulo || 'Aquí encuentras tus certificados y tu avance en cada curso.', size: 20, color: '#ffffff', peso: 'normal' },
    { visible: false, texto: '', size: 26, color: '#ffffff', peso: 'bold' },
  ];
  const headerCustom = front.header_modo === 'custom';
  // Estilo de las tarjetas del listado (todo opcional; sin config queda igual).
  const estiloTarjeta = {
    $fondo: front.lista_fondo_tarjeta,
    $borde: front.lista_borde,
    $radio: front.lista_radio,
    $padding: front.lista_padding,
    $separacion: front.lista_separacion,
    $sombra: front.lista_sombra,
    $sombraHover: front.lista_sombra_hover,
  };
  const heroVisible = front.hero_activo !== false;
  const footerModo = front.footer_modo || 'global';   // global | custom | none
  const footerCustom = footerModo === 'custom';

  // Banner prestado de otra página (front.banner_pagina = slug): se reutilizan
  // sus secciones de header y hero, con el título que se configure aquí. Así el
  // banner de certificados es el mismo de "Mi curso" sin duplicar su config.
  const bannerSlug = (front.banner_pagina || '').trim();
  const [bannerSecs, setBannerSecs] = useState([]);
  useEffect(() => {
    if (!bannerSlug || !proyecto?.id) { setBannerSecs([]); return undefined; }
    let vivo = true;
    misPaginasService.getBySlug(proyecto.id, bannerSlug, idioma)
      .then(res => {
        const pag = res.data?.pagina || (res.data?.paginas || [])[0];
        // `banner_logo` es el banner de portada que usan las páginas de Uroacademy;
    // se incluye para que el banner de certificados sea idéntico al del resto.
    const secs = (pag?.secciones || []).filter(x => ['header', 'hero', 'banner_logo'].includes(x.tipo) && x.visible !== false);
        if (vivo) setBannerSecs(secs);
      })
      .catch(() => { if (vivo) setBannerSecs([]); });
    return () => { vivo = false; };
  }, [bannerSlug, proyecto, idioma]);

  const bannerHeader = bannerSecs.some(x => x.tipo === 'header');

  // Header/footer propios → ocultar los globales del sitio para no duplicar.
  useEffect(() => {
    setPageLayout({ pageHasHeader: headerCustom || bannerHeader, pageHasFooter: footerModo !== 'global' });
    return () => setPageLayout({ pageHasHeader: false, pageHasFooter: false });
  }, [headerCustom, bannerHeader, footerModo, setPageLayout]);

  // ¿A qué certificados les falta la encuesta? Se consulta al cargar para poder
  // mostrar el aviso en lugar de los botones (el backend además lo bloquea).
  const [encPendiente, setEncPendiente] = useState({});   // {certId: true}
  useEffect(() => {
    const emitidos = data.emitidos || [];
    if (emitidos.length === 0) { setEncPendiente({}); return undefined; }
    let vivo = true;
    Promise.all(emitidos.map(c =>
      encuestaService.estado(c.proyecto_id, c.certificado_key || 'default')
        .then(r => [c.certificado_id, !!r.data?.requiere])
        .catch(() => [c.certificado_id, false])
    )).then(pares => {
      if (vivo) setEncPendiente(Object.fromEntries(pares.filter(([, v]) => v)));
    });
    return () => { vivo = false; };
  }, [data]);

  useEffect(() => {
    certificadoService.miProgreso(proyecto?.id)
      .then(res => setData({ emitidos: res.data?.emitidos || [], en_progreso: res.data?.en_progreso || [] }))
      .catch(e => console.error('Error cargando certificados:', e))
      .finally(() => setLoading(false));
  }, [proyecto]);

  const fetchPdf = useCallback(async (certId) => {
    const res = await certificadoService.descargarPDF(certId);
    return res.data;
  }, []);

  // Antes de ver/descargar: si el certificado exige encuesta y no está
  // respondida, abre el modal y ejecuta `proceed` recién tras responder.
  const conEncuesta = useCallback(async (item, proceed) => {
    const pid = item.proyecto_id;
    const key = item.certificado_key || 'default';
    try {
      const res = await encuestaService.estado(pid, key);
      if (res.data?.requiere) {
        setEncModal({ encuesta: res.data.encuesta, proyectoId: pid, certKey: key, certId: item.certificado_id, proceed });
        return;
      }
    } catch (e) { /* si el chequeo falla, no bloqueamos la descarga */ }
    proceed();
  }, []);

  const verPdf = async (certId) => {
    setBusy(`ver-${certId}`);
    try {
      const blob = await fetchPdf(certId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('No se pudo abrir el certificado');
    }
    setBusy(null);
  };

  const descargarPdf = async (certId, curso) => {
    setBusy(`dl-${certId}`);
    try {
      const blob = await fetchPdf(certId);
      downloadBlob(blob, `certificado_${(curso || 'curso').replace(/\s+/g, '_')}.pdf`, 'application/pdf');
    } catch (e) {
      toast.error('Error al descargar el certificado');
    }
    setBusy(null);
  };

  const abrirEncuesta = (c) => conEncuesta(c, () => verPdf(c.certificado_id));

  const handleVer = (c) => conEncuesta(c, () => verPdf(c.certificado_id));
  const handleDescargar = (c) => conEncuesta(c, () => descargarPdf(c.certificado_id, c.curso));

  const enviarEncuesta = async (respuestas) => {
    if (!encModal) return false;
    setEncSubmitting(true);
    let ok = false;
    try {
      await encuestaService.responder(encModal.proyectoId, encModal.certKey, respuestas);
      ok = true;
      // El certificado deja de estar bloqueado por la encuesta.
      setEncPendiente(prev => {
        const next = { ...prev };
        if (encModal.certId) delete next[encModal.certId];
        return next;
      });
      // Con la encuesta paso a paso, el modal muestra su propia pantalla final
      // con el botón de descarga; en el modo clásico se descarga de una vez.
      if (encModal.encuesta?.paso_a_paso === true) {
        setEncModal(prev => (prev ? { ...prev, respondida: true } : prev));
      } else {
        const proceed = encModal.proceed;
        setEncModal(null);
        toast.success('¡Gracias por tu respuesta!');
        if (proceed) proceed();
      }
    } catch (e) {
      toast.error('No se pudo guardar la encuesta');
    }
    setEncSubmitting(false);
    return ok;
  };

  const finalizarEncuesta = () => {
    const proceed = encModal?.proceed;
    setEncModal(null);
    if (proceed) proceed();
  };

  if (!user) return null;

  const { emitidos, en_progreso } = data;
  const MESES_POR_IDIOMA = {
    es: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
    pt: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  };
  const MESES = MESES_POR_IDIOMA[idioma] || MESES_POR_IDIOMA.es;
  const fmtFecha = (d) => { const dt = d ? new Date(d) : new Date(); return `${dt.getDate()} ${MESES[dt.getMonth()]} ${dt.getFullYear()}`; };
  // Lista unificada: solo certificados ASIGNADOS (emitidos con botones + en progreso con barra).
  const items = [
    ...emitidos.map(c => ({ ...c, _done: true })),
    ...en_progreso.map(c => ({ ...c, _done: false })),
  ];
  const nada = !loading && items.length === 0;

  // Resuelve un texto configurable: cadena tal cual, u objeto {es, pt, …} en el
  // idioma activo. Es lo que hace que la página entera se traduzca sin más que
  // guardar la versión en portugués desde el admin.
  const tlBanner = (v, fallback = '') => {
    if (v == null) return fallback;
    if (typeof v === 'string') return v || fallback;
    if (typeof v === 'object') return v[idioma] || v.es || fallback;
    return fallback;
  };
  // Atajo para los textos de esta página: t('texto_boton_ver', 'Ver Certificado').
  const t = (clave, porDefecto = '') => tlBanner(front[clave], porDefecto);
  const seccionesBanner = bannerSecs.map(sec => {
    if (sec.tipo !== 'hero' || !front.banner_titulo) return sec;
    return { ...sec, config: { ...(sec.config || {}), titulo: front.banner_titulo } };
  });

  // Fecha larga para la tabla: "2 de junio de 2026".
  const fmtFechaLarga = (d) => {
    const dt = d ? new Date(d) : new Date();
    // Español y portugués comparten la forma "4 de septiembre de 2026";
    // en inglés sería otra, pero la página se publica en es/pt.
    return `${dt.getDate()} de ${MESES[dt.getMonth()].toLowerCase()} de ${dt.getFullYear()}`;
  };

  // Lo que se comparte es la página de compartir del backend: LinkedIn no
  // ejecuta JavaScript, así que necesita un HTML con etiquetas Open Graph para
  // mostrar la imagen del certificado. Esa página redirige a la verificación.
  // /certificado/<codigo> es un enlace corto (.htaccess) que no muestra la ruta del backend.
  const urlCompartir = (c) => `${window.location.origin}/certificado/${encodeURIComponent(c.codigo || '')}`;
  // Para "añadir al perfil" LinkedIn pide la URL que acredita el certificado.
  const urlPublica = (c) => `${window.location.origin}/verificar/${c.codigo || ''}`;
  // Mensaje que va escrito en la publicación. `{curso}` usa el título impreso
  // en el certificado, no el del listado ("Certificado de …").
  const mensajeLinkedIn = (c) => (t('linkedin_texto') || '')
    .replace(/\{curso\}/g, c.titulo || c.curso || '')
    .replace(/\{certificado\}/g, c.curso || '')
    .replace(/\{nombre\}/g, user?.nombre || '')
    .replace(/\{proyecto\}/g, front.linkedin_organizacion || proyecto?.nombre || '')
    .replace(/\{fecha\}/g, fmtFechaLarga(c.fecha))
    .trim();

  const linkedinUrl = (c) => {
    if (front.linkedin_modo === 'perfil') {
      const d = c.fecha ? new Date(c.fecha) : new Date();
      const p = new URLSearchParams({
        startTask: 'CERTIFICATION_NAME',
        name: c.titulo || c.curso || '',
        organizationName: front.linkedin_organizacion || proyecto?.nombre || '',
        issueYear: String(d.getFullYear()),
        issueMonth: String(d.getMonth() + 1),
        certUrl: urlPublica(c),
        certId: c.codigo || '',
      });
      return `https://www.linkedin.com/profile/add?${p.toString()}`;
    }
    // Siempre share-offsite: es el que arma la tarjeta con la imagen del
    // certificado. El redactor con texto (feed/?shareActive&text=) casi nunca
    // la genera, así que el mensaje se copia al portapapeles (ver onClick).
    return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(urlCompartir(c))}`;
  };
  const linkedinVisible = front.mostrar_linkedin === true;

  // LinkedIn no deja precargar texto y tarjeta con imagen a la vez: share-offsite
  // pone la imagen y el texto (mensaje + enlace) va al portapapeles. Antes de
  // abrir LinkedIn se muestra ese texto para que el alumno sepa que debe pegarlo.
  const copiarTexto = (texto) => (navigator.clipboard
    ? navigator.clipboard.writeText(texto).then(() => true).catch(() => false)
    : Promise.resolve(false));

  const clicLinkedIn = (e, c) => {
    if (front.linkedin_modo === 'perfil') return;
    const mensaje = mensajeLinkedIn(c);
    if (!mensaje) return;
    e.preventDefault();
    const texto = `${mensaje}\n\n${urlCompartir(c)}`;
    setCompartirLi({ texto, url: linkedinUrl(c), copiado: false });
    copiarTexto(texto).then(ok => setCompartirLi(prev => prev && { ...prev, copiado: ok }));
  };

  const botonLinkedIn = (c) => (
    <BtnLinkedIn
      href={linkedinUrl(c)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={e => clicLinkedIn(e, c)}
      $bg={front.linkedin_color}
      $color={front.linkedin_color_texto}
      $borde={front.linkedin_borde}
      $radio={front.radio_boton}
      title={front.linkedin_modo === 'perfil' ? 'Añadir a mi perfil de LinkedIn' : 'Compartir en LinkedIn'}
    >
      {front.linkedin_icono !== false && <IconoLinkedIn />}
      {front.texto_linkedin != null ? t('texto_linkedin') : 'Compartir'}
    </BtnLinkedIn>
  );

  // --- Formato tabla (opt-in con front.lista_formato = 'tabla') -------------
  const esTabla = front.lista_formato === 'tabla';

  const filaTabla = (c) => {
    const hecho = c._done;
    const pct = hecho ? 100 : (c.porcentaje || 0);
    const claveEnc = hecho && encPendiente[c.certificado_id];
    return (
      <tr key={hecho ? `e-${c.certificado_id}` : `p-${c.proyecto_id}-${c.certificado_key || 'default'}`}>
        <td style={{ color: front.color_nombre || DEF.textLight }}>{c.curso}</td>
        <td>
          <PctChip $completo={pct >= 100} $bg={front.color_pill} $bgVacio={front.color_pill_vacio} $color={front.color_pct}>
            {pct}%
          </PctChip>
        </td>
        <td style={{ color: front.color_fecha || DEF.textLight }}>
          {hecho
            ? `${front.texto_obtenido != null ? t('texto_obtenido') : 'Obtenido el'} ${fmtFechaLarga(c.fecha)}`.trim()
            : (t('texto_estado_pendiente') || 'Progreso: {hechos} de {total} cursos completados')
                .replace('{hechos}', c.cumplidos ?? 0)
                .replace('{total}', c.total ?? 0)
                .replace('{pct}', pct)}
        </td>
        <td>
          {!hecho ? (
            <Pendiente $color={front.color_pendiente}>{t('texto_pendiente') || 'Pendiente'}</Pendiente>
          ) : claveEnc ? (
            <AvisoBoton
              $bg={front.aviso_color_boton}
              $color={front.aviso_color_texto_boton}
              $radio={front.radio_boton}
              $padding={front.aviso_padding_boton}
              $size={front.aviso_tamano_boton}
              onClick={() => abrirEncuesta(c)}
            >
              {t('aviso_texto_boton') || 'HAZ LA ENCUESTA AQUÍ'}
            </AvisoBoton>
          ) : (
            <Actions>
              <Btn $color={front.color_boton || color} $radio={front.radio_boton}
                disabled={busy === `ver-${c.certificado_id}`} onClick={() => handleVer(c)}>
                {busy === `ver-${c.certificado_id}` ? '...' : (t('texto_boton_ver') || 'Ver Certificado')}
              </Btn>
              {linkedinVisible && botonLinkedIn(c)}
              {front.mostrar_boton_descargar === true && (
                <Btn $color={front.color_boton || color} $radio={front.radio_boton} $ghost
                  disabled={busy === `dl-${c.certificado_id}`} onClick={() => handleDescargar(c)}>
                  {busy === `dl-${c.certificado_id}` ? '...' : (t('texto_boton_descargar') || 'Descargar')}
                </Btn>
              )}
            </Actions>
          )}
        </td>
      </tr>
    );
  };

  const modales = (
    <>
      {encModal && (
        <EncuestaModal
          encuesta={encModal.encuesta}
          color={color}
          submitting={encSubmitting}
          onSubmit={enviarEncuesta}
          onFinalizar={finalizarEncuesta}
          onClose={() => setEncModal(null)}
        />
      )}
      {compartirLi && (
        <FondoCompartir onClick={() => setCompartirLi(null)}>
          <CajaCompartir onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3>{idioma === 'pt' ? 'Compartilhar no LinkedIn' : 'Compartir en LinkedIn'}</h3>
            <p>
              {compartirLi.copiado
                ? (idioma === 'pt' ? 'Já copiamos este texto. No LinkedIn, cole-o (Ctrl+V ou toque longo → Colar) no campo da publicação; a imagem do certificado aparece sozinha.' : 'Ya copiamos este texto. En LinkedIn, pégalo (Ctrl+V o mantén presionado → Pegar) en el cuadro de la publicación; la imagen del certificado aparece sola.')
                : (idioma === 'pt' ? 'Copie este texto e cole-o no campo da publicação do LinkedIn; a imagem do certificado aparece sozinha.' : 'Copia este texto y pégalo en el cuadro de la publicación de LinkedIn; la imagen del certificado aparece sola.')}
            </p>
            <textarea readOnly value={compartirLi.texto} onFocus={e => e.target.select()} />
            <AccionesCompartir>
              <button type="button" onClick={() => copiarTexto(compartirLi.texto).then(ok => setCompartirLi(prev => prev && { ...prev, copiado: ok }))}>
                {compartirLi.copiado ? (idioma === 'pt' ? 'Copiado ✓' : 'Copiado ✓') : (idioma === 'pt' ? 'Copiar texto' : 'Copiar texto')}
              </button>
              <BtnLinkedIn
                href={compartirLi.url}
                target="_blank"
                rel="noopener noreferrer"
                $bg={front.linkedin_color}
                $color={front.linkedin_color_texto}
                $radio={front.radio_boton}
                onClick={() => { copiarTexto(compartirLi.texto); setCompartirLi(null); }}
              >
                <IconoLinkedIn />
                {idioma === 'pt' ? 'Abrir LinkedIn' : 'Abrir LinkedIn'}
              </BtnLinkedIn>
            </AccionesCompartir>
          </CajaCompartir>
        </FondoCompartir>
      )}
    </>
  );

  // Proyectos con portada estilo streaming (Learning): página propia con su
  // diseño oscuro; solo los certificados de ESTE proyecto. `front.diseno:
  // 'clasico'` vuelve al listado de siempre.
  const streaming = front.diseno !== 'clasico'
    && (proyecto?.config_json?.landing_secciones || []).some(x => x.tipo === 'catalogo_streaming' && x.visible !== false);
  if (streaming) {
    const delProyecto = (c) => Number(c.proyecto_id) === Number(proyecto.id);
    return (
      <>
        <CertificadosStreaming
          proyecto={proyecto}
          user={user}
          cargando={loading}
          emitidos={emitidos.filter(delProyecto)}
          enProgreso={en_progreso.filter(delProyecto)}
          busy={busy}
          encPendiente={encPendiente}
          onVer={handleVer}
          onDescargar={handleDescargar}
          onEncuesta={abrirEncuesta}
          linkedinVisible={linkedinVisible}
          linkedinUrl={linkedinUrl}
          onLinkedIn={clicLinkedIn}
          mostrarDescargar={front.mostrar_boton_descargar !== false}
        />
        {modales}
      </>
    );
  }

  return (
    <Wrap $bg={front.lista_fondo} $font={front.fuente}>
      {seccionesBanner.map((sec, i) => (
        <SectionRenderer key={sec.id || i} seccion={sec} proyecto={proyecto} tl={tlBanner} />
      ))}

      {headerCustom && (
        <CustomHeader $color={color}>
          {proyecto?.logo && <img src={`${BACKEND_BASE}/${proyecto.logo}`} alt="" />}
          <span>{front.header_titulo || proyecto?.nombre || 'Certificados'}</span>
        </CustomHeader>
      )}

      {heroVisible && seccionesBanner.length === 0 && (
        <Hero
          $color={color}
          $img={heroImg}
          $altura={front.hero_altura || 240}
          $align={front.hero_align || 'left'}
          $overlay={!!front.hero_overlay}
          $opac={(front.hero_overlay_opacidad ?? 40) / 100}
          $pos={front.hero_bg_pos || 'center'}
          $size={front.hero_bg_size || 'cover'}
        >
          {heroTextos.map((t, i) => (t.visible !== false && (t.texto || '').trim()) ? (
            <HeroText key={i} $size={t.size || 24} $color={t.color || '#fff'} $peso={t.peso || 'bold'}>{t.texto}</HeroText>
          ) : null)}
        </Hero>
      )}

      <Container $max={front.ancho_maximo} $padding={front.lista_padding_contenedor}>
        {front.titulo_lista && (
          <TituloLista
            $color={front.titulo_lista_color}
            $size={front.titulo_lista_tamano}
            $margen={front.titulo_lista_margen}
            $transform={front.titulo_lista_transform}
            $linea={front.titulo_lista_linea}
            $lineaAlto={front.titulo_lista_linea_alto}
          >
            {t('titulo_lista')}
          </TituloLista>
        )}
        {loading ? (
          <p style={{ textAlign: 'center', color: DEF.textLight, padding: '2rem' }}>{tUI('cargando')}</p>
        ) : nada ? (
          <Empty>{tUI('certificados_vacios')}</Empty>
        ) : esTabla ? (
          <TablaWrap $borde={front.tabla_borde} $radio={front.tabla_radio} $fondo={front.tabla_fondo}>
            <Tabla
              $size={front.tabla_tamano}
              $padding={front.tabla_padding}
              $borde={front.tabla_borde}
              $colorTh={front.tabla_color_encabezado}
              $fondoTh={front.tabla_fondo_encabezado}
              $fondoPar={front.tabla_fondo_par}
              $colorTd={front.tabla_color_texto}
            >
              <thead>
                <tr>
                  <th>{t('tabla_col_nombre') || 'Nombre'}</th>
                  <th>{t('tabla_col_progreso') || 'Progreso'}</th>
                  <th>{t('tabla_col_estado') || 'Estado'}</th>
                  <th>{t('tabla_col_acciones') || 'Acciones'}</th>
                </tr>
              </thead>
              <tbody>{items.map(filaTabla)}</tbody>
            </Tabla>
          </TablaWrap>
        ) : (
          items.map(c => c._done ? (encPendiente[c.certificado_id] ? (
            <AvisoEncuesta
              key={`enc-${c.certificado_id}`}
              $fondo={front.aviso_fondo}
              $radio={front.aviso_radio}
              $sombra={front.aviso_sombra || front.lista_sombra}
              $padding={front.aviso_padding}
            >
              {front.aviso_icono !== false && (
                <AvisoIcono $color={front.aviso_color_icono || front.aviso_color}><IconoX /></AvisoIcono>
              )}
              <AvisoTexto $color={front.aviso_color} $size={front.aviso_tamano} $peso={front.aviso_peso}>
                {t('aviso_texto') || 'Complete la encuesta de satisfacción para acceder a la descarga de su certificado.'}
              </AvisoTexto>
              <AvisoBoton
                $bg={front.aviso_color_boton}
                $color={front.aviso_color_texto_boton}
                $radio={front.aviso_radio_boton}
                $padding={front.aviso_padding_boton}
                $size={front.aviso_tamano_boton}
                onClick={() => abrirEncuesta(c)}
              >
                {t('aviso_texto_boton') || 'HAZ LA ENCUESTA AQUÍ'}
              </AvisoBoton>
            </AvisoEncuesta>
          ) : (
            <Card key={`e-${c.certificado_id}`} $color={color} {...estiloTarjeta}>
              <CardBody><CursoName $color={front.color_nombre}>{c.curso}</CursoName></CardBody>
              {front.mostrar_pct !== false && <Pill $bg={front.color_pill}>100%</Pill>}
              <DateCol $color={front.color_fecha}>
                {`${front.texto_obtenido != null ? t('texto_obtenido') : 'Obtenido el'} ${fmtFecha(c.fecha)}`.trim()}
              </DateCol>
              <Actions>
                <Btn $color={front.color_boton || color} $radio={front.radio_boton}
                  disabled={busy === `ver-${c.certificado_id}`} onClick={() => handleVer(c)}>
                  {busy === `ver-${c.certificado_id}` ? '...' : (t('texto_boton_ver') || 'Ver Certificado')}
                </Btn>
                {linkedinVisible && botonLinkedIn(c)}
                {front.mostrar_boton_descargar !== false && (
                  <Btn $color={front.color_boton || color} $radio={front.radio_boton} $ghost
                    disabled={busy === `dl-${c.certificado_id}`} onClick={() => handleDescargar(c)}>
                    {busy === `dl-${c.certificado_id}` ? '...' : (t('texto_boton_descargar') || 'Descargar')}
                  </Btn>
                )}
              </Actions>
            </Card>
          )) : (
            <Card key={`p-${c.proyecto_id}-${c.certificado_key || 'default'}`} $color={color} {...estiloTarjeta}>
              <DateCol>{fmtFecha()}</DateCol>
              <Divider $color={color} />
              <CardBody>
                <CursoName>{c.curso}</CursoName>
                <Bar><Fill $pct={c.porcentaje} $color={color} /></Bar>
                <Falta>
                  {c.pendientes && c.pendientes.length > 0
                    ? `Falta: ${c.pendientes.slice(0, 3).join(' · ')}${c.pendientes.length > 3 ? ` y ${c.pendientes.length - 3} más` : ''}`
                    : `Te falta ${100 - c.porcentaje}% para completarlo.`}
                </Falta>
              </CardBody>
              <Actions>
                <PctTag $color={color}>{c.porcentaje}%</PctTag>
              </Actions>
            </Card>
          ))
        )}
      </Container>

      {footerCustom && (
        <CustomFooter $color={front.footer_color || color}>
          {front.footer_logo !== false && proyecto?.logo && <img src={`${BACKEND_BASE}/${proyecto.logo}`} alt="" />}
          <span>{front.footer_texto || `© ${new Date().getFullYear()} ${proyecto?.nombre || ''}`}</span>
        </CustomFooter>
      )}

      {modales}
    </Wrap>
  );
};

export default MisCertificadosPage;
